
import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { AuditLog, ChangedAttribute } from '../../../../../models/audit-log.model';
import { CommonService } from 'src/app/services/common.service';

interface DisplayableAuditLog {
  log: AuditLog;
  changes: string[];
}

@Component({
  selector: 'regulations-changes-logs',
  imports: [],
  templateUrl: './regulations-changes-logs.component.html',
  styleUrl: './regulations-changes-logs.component.scss'
})
export class RegulationsChangesLogComponent implements OnInit, OnChanges {
  @Input() auditLogs: AuditLog[] = [];
  formattedLogs: DisplayableAuditLog[] = [];
  isLoading: boolean = false;
  errorMessage: string | null = null;

  constructor(private commonService: CommonService) {}

  ngOnInit(): void {
    this.processAuditLogs();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['auditLogs']) {
      this.processAuditLogs();
    }
  }

  private processAuditLogs(): void {
    if (!this.auditLogs || this.auditLogs.length === 0) {
      this.errorMessage = 'Nessun changelog disponibile';
      this.formattedLogs = [];
      return;
    }

    this.errorMessage = null;
    this.formattedLogs = this.auditLogs.map((log) => ({
      log,
      changes: this.buildChangeEntries(log)
    }));
  }

  getUserDisplayName(auditLog: AuditLog): string {
    const user = auditLog.creatorUser;
    return `${user.lastName}  ${user.firstName}`.trim() || user.email || 'Unknown user';
  }

  formatDate(date: Date | string): string {
    if (!date) return '-';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) {
      return '-';
    }

    return this.commonService.formatDate(dateObj);
  }
  getEventLabel(event: string): string {
    const eventLabels: { [key: string]: string } = {
      'INSERT': 'Creazione',
      'UPDATE': 'Modifica',
      'DELETE': 'Eliminazione'
    };
    return eventLabels[event] || event;
  }

  getEventBadgeClass(event: string): string {
    const eventClasses: { [key: string]: string } = {
      'INSERT': 'badge-success',
      'UPDATE': 'badge-info',
      'DELETE': 'badge-danger'
    };
    return eventClasses[event] || 'badge-secondary';
  }

  private buildChangeEntries(auditLog: AuditLog): string[] {
    if (!auditLog.changedData || auditLog.changedData.length === 0) {
      return ['Nessuna modifica disponibile'];
    }

    return auditLog.changedData.map((changedAttribute) => {
      const event = auditLog.event === 'INSERT' ? 'ADD' : auditLog.event;
      const attribute = changedAttribute.attributeDescription || changedAttribute.attribute || 'Campo';
      const value = this.resolveChangeValue(auditLog.event, changedAttribute);
      return `${event} ${attribute} - ${value}`;
    });
  }

  private resolveChangeValue(event: AuditLog['event'], change: ChangedAttribute): string {
    const value = event === 'DELETE'
      ? change.oldValue
      : (change.newValue ?? change.oldValue);

    return this.stringifyValue(value);
  }

  private stringifyValue(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    if (Array.isArray(value)) {
      if (!value.length) {
        return '-';
      }
      return value
        .map((v) => this.stringifyValue(v))
        .join(', ');
    }

    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch (_) {
        return '-';
      }
    }

    return String(value);
  }
}
